import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { signInWithEmailAndPassword, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../utils/firebase';
import { getAdminLoginErrorMessage, GENERIC_LOGIN_ERROR } from '../utils/loginErrors';
import { isExerciseEditor } from '../config/adminRoles';
import { useAuth } from '../contexts/AuthContext';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, adminRole, checking, refreshAdminSession } = useAuth();

  const redirectAfterLogin = useCallback((role) => {
    navigate(isExerciseEditor(role) ? '/admin/exercises' : '/admin', { replace: true });
  }, [navigate]);

  // Redirection quand le contexte auth est à jour (évite la course après signInWithPopup)
  useEffect(() => {
    if (!checking && user && isAdmin) {
      redirectAfterLogin(adminRole);
    }
  }, [checking, user, isAdmin, adminRole, redirectAfterLogin]);

  const verifyAdminAccess = async (firebaseUser) => {
    const session = await refreshAdminSession(firebaseUser);
    if (!session.isAdmin) {
      await signOut(auth);
      throw new Error(GENERIC_LOGIN_ERROR);
    }
    return session.adminRole;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const adminRole = await verifyAdminAccess(credential.user);
      redirectAfterLogin(adminRole);
    } catch (err) {
      const message = getAdminLoginErrorMessage(err);
      if (message) setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const adminRole = await verifyAdminAccess(credential.user);
      redirectAfterLogin(adminRole);
    } catch (err) {
      const message = getAdminLoginErrorMessage(err);
      if (message) setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            DictoQuest - Interface Admin
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Connectez-vous avec vos identifiants administrateur
          </Typography>

          {import.meta.env.DEV && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Mode local : Auth émulateur + backend (tous les comptes authentifiés sont admin).
              Utilisez un compte créé dans l&apos;émulateur (email/mot de passe).
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{ mb: 3 }}
          >
            Continuer avec Google
          </Button>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary">ou</Typography>
          </Divider>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}
